import { styled } from '@mui/material';
import { cloneElement, forwardRef } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

const VARIANTS = ['primary', 'neutral'];

const CustomA = styled('a')(({ disabled, variant, theme }) => {
    return {
        '&:focus-visible': {
            borderRadius: '4px',
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
        ...{
            primary: {
                textDecorationColor: 'currentColor',
                color: theme.palette.ensightful.primary[500],
                '&:hover': {
                    textDecoration: 'underline',
                    color: theme.palette.ensightful.primary[700],
                },
                '&:active': {
                    textDecoration: 'underline',
                    color: theme.palette.ensightful.primary[800],
                },
            },
            neutral: {
                textDecorationColor: 'currentColor',
                color: theme.palette.ensightful.neutral[700],
                '&:hover': {
                    textDecoration: 'underline',
                    color: theme.palette.ensightful.neutral[800],
                },
                '&:active': {
                    textDecoration: 'underline',
                    color: theme.palette.ensightful.primary[900],
                },
            },
        }[variant],
        ...(disabled && {
            textDecoration: 'none',
            color: theme.palette.ensightful.neutral[400],
            cursor: 'default',
            pointerEvents: 'none',
            '&:hover': {
                color: theme.palette.ensightful.neutral[400],
            },
            '&:active': {
                color: theme.palette.ensightful.neutral[400],
            },
        }),
    };
});

const CustomReactRouterLink = styled(ReactRouterLink)(({ disabled, variant, theme }) => {
    return {
        '&:focus-visible': {
            borderRadius: '4px',
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
        ...{
            primary: {
                textDecorationColor: 'currentColor',
                color: theme.palette.ensightful.primary[500],
                '&:hover': {
                    textDecoration: 'underline',
                    color: theme.palette.ensightful.primary[700],
                },
                '&:active': {
                    textDecoration: 'underline',
                    color: theme.palette.ensightful.primary[800],
                },
            },
            neutral: {
                textDecorationColor: 'currentColor',
                color: theme.palette.ensightful.neutral[700],
                '&:hover': {
                    textDecoration: 'underline',
                    color: theme.palette.ensightful.neutral[800],
                },
                '&:active': {
                    textDecoration: 'underline',
                    color: theme.palette.ensightful.primary[900],
                },
            },
        }[variant],
        ...(disabled && {
            textDecoration: 'none',
            color: theme.palette.ensightful.neutral[400],
            cursor: 'default',
            pointerEvents: 'none',
            '&:hover': {
                color: theme.palette.ensightful.neutral[400],
            },
            '&:active': {
                color: theme.palette.ensightful.neutral[400],
            },
        }),
    };
});

const Link = forwardRef(({ children, endIcon, external, startIcon, variant, ...rest }, ref) => {
    return external ? (
        <CustomA ref={ref} variant={variant} {...rest}>
            {startIcon && cloneElement(startIcon, { sx: { mb: '-0.1rem', mr: 1, fontSize: '0.75rem' } })}
            {children}
            {endIcon && cloneElement(endIcon, { sx: { mb: '-0.1rem', ml: 1, fontSize: '0.75rem' } })}
        </CustomA>
    ) : (
        <CustomReactRouterLink ref={ref} variant={variant} {...rest}>
            {startIcon && cloneElement(startIcon, { sx: { mb: '-0.1rem', mr: 1, fontSize: '0.75rem' } })}
            {children}
            {endIcon && cloneElement(endIcon, { sx: { mb: '-0.1rem', ml: 1, fontSize: '0.75rem' } })}
        </CustomReactRouterLink>
    );
});

export default forwardRef(({ disabled, variant = 'primary', ...rest }, ref) => {
    if (!VARIANTS.includes(variant)) {
        throw new Error(`Shell: Invalid color "${variant}" in component Link.`);
    }

    return (
        <Link
            ref={ref}
            disabled={disabled}
            variant={variant}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={(e) => {
                if (disabled && e.key === 'Enter') {
                    e.preventDefault();
                }
            }}
            {...rest}
        />
    );
});
